import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { vacancyCandidatesAPi } from '../services/vacancyCandidates';
import { vacanciesApi } from '../services/vacancies';
import { logout } from './authSlice';

// ===== THUNKS =====

export const fetchCandidatesByVacancy = createAsyncThunk(
    'vacancyCandidates/fetchCandidatesByVacancy',
    async (vacancyId, { rejectWithValue }) => {
        try {
            return await vacancyCandidatesAPi.getCandidatesByVacancy(vacancyId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVacancyByCandidate = createAsyncThunk(
    'vacancyCandidates/fetchVacancyByCandidate',
    async (candidateId, { rejectWithValue }) => {
        try {
            return await vacancyCandidatesAPi.getVacancyByCandidate(candidateId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const createVacancyCandidate = createAsyncThunk(
    'vacancyCandidates/create',
    async ({ vacancyId, candidateId }, { rejectWithValue }) => {
        try {
            return await vacancyCandidatesAPi.create(vacancyId, candidateId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateVacancyCandidateStatus = createAsyncThunk(
    'vacancyCandidates/updateStatus',
    async ({ vacancyId, candidateId, statusId, completionData }, { rejectWithValue }) => {
        try {
            // Сначала обновляем статус
            const statusResult = await vacancyCandidatesAPi.updateCandidateStatus(vacancyId, candidateId, statusId);

            // Если есть данные о результате завершения, обновляем их отдельным запросом
            if (completionData) {
                const updateData = {};

                if (completionData.result === 'success') {
                    updateData.result_candidate = 'success';
                    if (completionData.completion_work) {
                        updateData.completion_work = completionData.completion_work;
                    }
                } else if (completionData.result === 'failure') {
                    updateData.result_candidate = 'failure';
                    updateData.failure_reason = completionData.failure_reason;
                } else if (completionData.result === 'reserve') {
                    updateData.result_candidate = 'reserved';
                }

                // Делаем второй запрос для обновления данных о результате
                await vacancyCandidatesAPi.updateVacancyCandidate(vacancyId, candidateId, updateData);
            }

            return statusResult;
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchAllowedFunnelStatuses = createAsyncThunk(
    'vacancyCandidates/fetchAllowedFunnelStatuses',
    async (vacancyId, { rejectWithValue }) => {
        try {
            return await vacancyCandidatesAPi.getAllowedFunnelStatuses(vacancyId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVacanciesByIds = createAsyncThunk(
    'vacancyCandidates/fetchVacanciesByIds',
    async (vacancyIds, { rejectWithValue }) => {
        try {
            return await vacanciesApi.getVacanciesByIds(vacancyIds);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const vacancyCandidateReturnToWork = createAsyncThunk(
    'vacancyCandidates/vacancyCandidateReturnToWork',
    async ({ vacancyId, candidateId, comment }, { rejectWithValue }) => {
        try {
            return await vacancyCandidatesAPi.vacancyCandidateReturnToWork(vacancyId, candidateId, comment);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

const initialState = {
    candidatesByVacancy: [],
    vacancyByCandidate: [],
    allowedFunnelStatuses: [],
    vacancies: [],
    isLoading: false,
    error: null
};

const vacancyCandidatesSlice = createSlice({
    name: 'vacancyCandidates',
    initialState,
    reducers: {
        clearCurrent: (state) => {
            state.vacancyByCandidate = null;
            state.error = null;
        },
        clearAllowedFunnelStatuses: (state) => {
            state.allowedFunnelStatuses = [];
        },
        updateVacancyCandidateStatusOptimistic: (state, action) => {
            const { vacancyId, candidateId, statusId } = action.payload;
            const index = state.candidatesByVacancy.findIndex(item =>
                item.vacancy_id === vacancyId && item.candidate_id === candidateId
            );
            if (index !== -1) state.candidatesByVacancy[index].status_id = statusId;
        },
        revertVacancyCandidateStatus: (state, action) => {
            const { vacancyId, candidateId, originalStatusId } = action.payload;
            const index = state.candidatesByVacancy.findIndex(item =>
                item.vacancy_id === vacancyId && item.candidate_id === candidateId
            );
            if (index !== -1) state.candidatesByVacancy[index].status_id = originalStatusId;
        }
    },
    extraReducers: (builder) => {
        builder

            // Get Candidates by Vacancy
            .addCase(fetchCandidatesByVacancy.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchCandidatesByVacancy.fulfilled, (state, action) => {
                state.isLoading = false;
                state.candidatesByVacancy = action.payload;
            })
            .addCase(fetchCandidatesByVacancy.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get Vacancy by Candidate
            .addCase(fetchVacancyByCandidate.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchVacancyByCandidate.fulfilled, (state, action) => {
                state.isLoading = false;
                state.vacancyByCandidate = action.payload;
            })
            .addCase(fetchVacancyByCandidate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Create
            .addCase(createVacancyCandidate.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createVacancyCandidate.fulfilled, (state, action) => {
                state.isLoading = false;
                // После создания связи кандидат-вакансия добавляем её в список
                if (action.payload && action.payload.data) {
                    state.candidatesByVacancy.push(action.payload.data);
                } else if (action.payload) {
                    state.candidatesByVacancy.push(action.payload);
                }
            })
            .addCase(createVacancyCandidate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Update Status
            .addCase(updateVacancyCandidateStatus.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateVacancyCandidateStatus.fulfilled, (state, action) => {
                state.isLoading = false;
                // Обновляем статус кандидата в списке
                const { vacancyId, candidateId } = action.meta.arg;
                const index = state.candidatesByVacancy.findIndex(item =>
                    item.vacancy_id === vacancyId && item.candidate_id === candidateId
                );
                if (index !== -1 && action.payload) {
                    state.candidatesByVacancy[index] = { ...state.candidatesByVacancy[index], ...action.payload };
                }
            })
            .addCase(updateVacancyCandidateStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get Allowed Funnel Statuses
            .addCase(fetchAllowedFunnelStatuses.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchAllowedFunnelStatuses.fulfilled, (state, action) => {
                state.isLoading = false;
                state.allowedFunnelStatuses = action.payload;
            })
            .addCase(fetchAllowedFunnelStatuses.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get Vacancies by IDs
            .addCase(fetchVacanciesByIds.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchVacanciesByIds.fulfilled, (state, action) => {
                state.isLoading = false;
                state.vacancies = action.payload;
            })
            .addCase(fetchVacanciesByIds.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Vacancy Candidate Return to Work
            .addCase(vacancyCandidateReturnToWork.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(vacancyCandidateReturnToWork.fulfilled, (state, action) => {
                state.isLoading = false;
            })
            .addCase(vacancyCandidateReturnToWork.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Обработка logout из authSlice
            .addCase(logout, (state) => {
                state.list = [];
                state.candidatesByVacancy = [];
                state.current = null;
                state.allowedFunnelStatuses = [];
                state.isLoading = false;
                state.error = null;
            });
    }
});

export const { clearCurrent, clearAllowedFunnelStatuses, updateVacancyCandidateStatusOptimistic, revertVacancyCandidateStatus } = vacancyCandidatesSlice.actions;
export const vacancyCandidatesReducer = vacancyCandidatesSlice.reducer; 